import {
    LineChart,
    BarChart,
    DoughnutChart,
    PieChart,
    RadarChart,
    PolarAreaChart,
    HeatmapChart,
    DataTableChart,
    HeroCard,
    AnalyticsOverviewCard,
    MetricsTableChart,
} from '../components/charts';
import { legendBottom, legendBottomCompact } from '../utils/chartDefaults';
import { Activity, Zap, Store, Clock, TrendingUp, MousePointer2 } from 'lucide-react';
import { palette } from '../constants/colors';

/**
 * Chart Library catalog. Each entry's `id` is the stable template key used by
 * codegen to match per-database notes. `component` references the reusable
 * chart component in components/charts/.
 *
 * Sample datasets here carry NO colors. The wrapper components auto-apply the
 * theme palette via themeDatasets() — change `constants/colors.js` and every
 * preview repaints. Keep only data, labels, and non-color styling here.
 */

const basicLineChart = {
    id: 'basic-line',
    title: 'Basic Line Chart',
    type: 'LINE',
    category: 'Line',
    component: LineChart,
    data: {
        labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        datasets: [{
            data: [120, 200, 150, 80, 70, 110, 130],
            borderWidth: 2.5,
            pointBorderWidth: 2,
        }],
    },
    options: {},
    code: `let option = {
  type: 'line',
  data: {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [{ data: [120, 200, 150, 80, 70, 110, 130] }]
  }
};`,
};

const areaStackedChart = {
    id: 'area-stacked',
    title: 'Area Stacked Chart',
    type: 'LINE',
    category: 'Line',
    component: LineChart,
    data: {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        datasets: [
            { label: 'Group A', data: [45, 52, 38, 24, 33, 10], fill: true },
            { label: 'Group B', data: [80, 92, 100, 75, 60, 28], fill: true },
        ],
    },
    options: { plugins: { legend: legendBottomCompact } },
    code: `let option = {
  type: 'line',
  data: {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      { label: 'Group A', data: [45, 52, 38, 24, 33, 10], fill: true },
      { label: 'Group B', data: [80, 92, 100, 75, 60, 28], fill: true }
    ]
  }
};`,
};

const gradientLineChart = {
    id: 'gradient-line',
    title: 'Gradient Smooth Line',
    type: 'LINE',
    category: 'Line',
    component: LineChart,
    data: {
        labels: ['', '', '', '', '', '', ''],
        datasets: [{
            data: [40, 60, 55, 30, 75, 60, 95],
            borderWidth: 3,
            pointRadius: 0,
            fill: true,
        }],
    },
    options: { scales: { x: { display: false }, y: { display: false } } },
    code: `let option = {
  type: 'line',
  data: {
    datasets: [{
      data: [40, 60, 55, 30, 75, 60, 95],
      fill: true
    }]
  }
};`,
};

const roundedBarChart = {
    id: 'rounded-bar',
    title: 'Rounded Bar Chart',
    type: 'BAR',
    category: 'Bar',
    component: BarChart,
    data: {
        labels: ['A', 'B', 'C', 'D', 'E'],
        datasets: [{
            label: 'Revenue',
            data: [65, 45, 75, 50, 90],
            borderRadius: 8,
        }],
    },
    options: {},
    code: `let option = {
  type: 'bar',
  data: {
    labels: ['A', 'B', 'C', 'D', 'E'],
    datasets: [{ data: [65, 45, 75, 50, 90], borderRadius: 8 }]
  }
};`,
};

const stackedPerformanceChart = {
    id: 'stacked-performance',
    title: 'Stacked Performance',
    type: 'BAR',
    category: 'Bar',
    component: BarChart,
    data: {
        labels: ['Q1', 'Q2', 'Q3', 'Q4'],
        datasets: [
            { label: 'Direct', data: [120, 130, 100, 110] },
            { label: 'Email', data: [220, 180, 190, 210] },
            { label: 'Social', data: [150, 230, 200, 200] },
        ],
    },
    options: {
        scales: { x: { stacked: true }, y: { stacked: true } },
        plugins: { legend: legendBottomCompact },
    },
    code: `let option = {
  type: 'bar',
  scales: { x: { stacked: true }, y: { stacked: true } },
  data: { labels: ['Q1', 'Q2', 'Q3', 'Q4'], datasets: [/* 3 series */] }
};`,
};

const marketSharePieChart = {
    id: 'market-share-pie',
    title: 'Market Share Pie',
    type: 'PIE',
    category: 'Pie / Doughnut',
    component: PieChart,
    data: {
        labels: ['Search', 'Direct', 'Video', 'Social'],
        datasets: [{ data: [45, 25, 20, 10], borderWidth: 0 }],
    },
    options: { plugins: { legend: legendBottom } },
    code: `let option = {
  type: 'pie',
  data: {
    labels: ['Search', 'Direct', 'Video', 'Social'],
    datasets: [{ data: [45, 25, 20, 10] }]
  }
};`,
};

const customDoughnutChart = {
    id: 'custom-doughnut',
    title: 'Custom Doughnut',
    type: 'DOUGHNUT',
    category: 'Pie / Doughnut',
    component: DoughnutChart,
    data: {
        labels: ['Completed', 'Pending', 'Failed'],
        datasets: [{ data: [65, 20, 15], borderWidth: 0 }],
    },
    options: {
        elements: { center: { text: '65%', subText: 'COMPLETED', fontSize: 28, subFontSize: 10, yOffset: -5 } },
    },
    code: `let option = {
  type: 'doughnut',
  data: {
    labels: ['Completed', 'Pending', 'Failed'],
    datasets: [{ data: [65, 20, 15] }]
  }
};`,
};

const performanceGaugeChart = {
    id: 'performance-gauge',
    title: 'Performance Gauge',
    type: 'DOUGHNUT',
    category: 'Pie / Doughnut',
    component: DoughnutChart,
    data: {
        labels: ['Fulfilled', 'Remaining'],
        datasets: [{
            data: [85.77, 14.23],
            borderWidth: 0,
            circumference: 180,
            rotation: -90,
        }],
    },
    options: {
        layout: { padding: { top: 4, bottom: 4 } },
        elements: { center: { text: '85.77%', subText: 'Fulfillment Score', fontSize: 22, subFontSize: 8, yOffset: 12 } },
    },
    code: `let option = {
  type: 'doughnut',
  data: { labels: ['Fulfilled', 'Remaining'], datasets: [{ data: [85.77, 14.23], circumference: 180, rotation: -90 }] }
};`,
};

const capabilityRadarChart = {
    id: 'capability-radar',
    title: 'Capability Radar',
    type: 'RADAR',
    category: 'Radar / Polar',
    component: RadarChart,
    data: {
        labels: ['Speed', 'Reliability', 'Comfort', 'Safety', 'Tech', 'Price'],
        datasets: [
            { label: 'Model X', data: [90, 85, 70, 95, 80, 60], fill: true },
            { label: 'Model Y', data: [70, 75, 90, 80, 95, 85], fill: true },
        ],
    },
    options: { plugins: { legend: legendBottomCompact } },
    code: `let option = {
  type: 'radar',
  data: { labels: ['Speed', 'Reliability', 'Comfort', 'Safety', 'Tech', 'Price'], datasets: [/* 2 models */] }
};`,
};

const polarAreaChart = {
    id: 'polar-area',
    title: 'Polar Area Distribution',
    type: 'POLARAREA',
    category: 'Radar / Polar',
    component: PolarAreaChart,
    data: {
        labels: ['Direct', 'Referral', 'Social', 'Email', 'Other'],
        datasets: [{ data: [80, 65, 45, 90, 30], borderWidth: 0 }],
    },
    options: {},
    code: `let option = {
  type: 'polarArea',
  data: { datasets: [{ data: [80, 65, 45, 90, 30] }] }
};`,
};

const mixedBarLineChart = {
    id: 'mixed-bar-line',
    title: 'Mixed Bar-Line',
    type: 'BAR',
    category: 'Bar',
    component: BarChart,
    data: {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        datasets: [
            { label: 'Sales', type: 'bar', data: [450, 520, 380, 240, 330, 410], borderRadius: 4, order: 2 },
            { label: 'Trend', type: 'line', data: [440, 480, 420, 300, 360, 450], borderWidth: 2, fill: false, pointBorderWidth: 2, tension: 0.4, order: 1 },
        ],
    },
    options: { plugins: { legend: legendBottomCompact } },
    code: `let option = {
  type: 'bar',
  data: { datasets: [{ type: 'bar', label: 'Sales', data: [...] }, { type: 'line', label: 'Trend', data: [...] }] }
};`,
};

// Showcase heatmap values sourced here (not from HeatmapChart's internal generator).
const heatmapDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const heatmapHours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const heatmapValues = heatmapDays.map((_day, d) =>
    heatmapHours.map((_hour, h) => {
        const base = ((d * 5 + h * 3) % 40) + 40;
        return h >= 9 && h <= 18 ? base + 20 : base;
    }),
);

const basicDataTable = {
    id: 'basic-data-table',
    title: 'Basic Data Table',
    type: 'TABLE',
    category: 'Table',
    component: DataTableChart,
    data: {
        columns: [
            { key: 'restaurant', label: 'Restaurant', emphasis: 'primary' },
            { key: 'status', label: 'Status', align: 'center', type: 'status' },
            { key: 'orders', label: 'Orders', align: 'right' },
            { key: 'revenue_k', label: 'Revenue (K)', align: 'right' },
            { key: 'avg_items', label: 'Avg Items', align: 'right' },
            { key: 'share_pct', label: 'Share %', align: 'right', emphasis: 'strong' },
        ],
        rows: [
            { restaurant: 'Hydrabadi Dum Biryani Butwal', status: 'Active', orders: 385, revenue_k: '34349.0', avg_items: '1.5', share_pct: '21.6%' },
            { restaurant: 'KFC Chitwan', status: 'Active', orders: 186, revenue_k: '18240.5', avg_items: '2.3', share_pct: '15.4%' },
            { restaurant: 'Pizza Hut Kathmandu', status: 'Pending', orders: 172, revenue_k: '16890.0', avg_items: '2.1', share_pct: '12.8%' },
            { restaurant: 'Burger House Pokhara', status: 'Active', orders: 158, revenue_k: '14220.0', avg_items: '1.9', share_pct: '11.2%' },
            { restaurant: 'Momo Corner Lalitpur', status: 'Inactive', orders: 143, revenue_k: '12850.5', avg_items: '1.7', share_pct: '9.8%' },
            { restaurant: 'Sushi Express Bhaktapur', status: 'Active', orders: 121, revenue_k: '11420.0', avg_items: '2.0', share_pct: '8.4%' },
            { restaurant: 'Thakali Kitchen Dharan', status: 'Pending', orders: 98, revenue_k: '9120.0', avg_items: '1.6', share_pct: '6.9%' },
            { restaurant: 'Cafe Sunrise Biratnagar', status: 'Active', orders: 87, revenue_k: '7840.5', avg_items: '1.4', share_pct: '5.5%' },
            { restaurant: 'Tandoor Express Hetauda', status: 'Inactive', orders: 76, revenue_k: '6920.0', avg_items: '1.8', share_pct: '4.8%' },
            { restaurant: 'Noodle Bar Itahari', status: 'Active', orders: 64, revenue_k: '5480.0', avg_items: '1.5', share_pct: '3.6%' },
        ],
    },
    options: { variant: 'report', striped: false },
    reportCodegenDefaultHint:
        'Render tabular query results with DataTableChart (variant report): first column left-aligned semibold label; numeric columns right-aligned with tabular-nums; final share/ratio column bold. Map query columns to `columns` (key, label, align, emphasis) and rows from result data only.',
    code: `// Basic Data Table (report style)
let table = {
  columns: [
    { key: 'restaurant', label: 'Restaurant', emphasis: 'primary' },
    { key: 'orders', label: 'Orders', align: 'right' },
    { key: 'share_pct', label: 'Share %', align: 'right', emphasis: 'strong' }
  ],
  rows: [{ restaurant: 'Example', orders: 385, share_pct: '21.6%' }],
  options: { variant: 'report', striped: false }
};`,
};

const stripedMetricsTable = {
    id: 'striped-metrics',
    title: 'Striped Metrics Table',
    type: 'TABLE',
    category: 'Table',
    component: MetricsTableChart,
    data: {
        columns: [
            { key: 'metric', label: 'Metric', emphasis: 'primary' },
            { key: 'current', label: 'Current', align: 'right' },
            { key: 'previous', label: 'Previous', align: 'right' },
            { key: 'change', label: 'Change', align: 'right', emphasis: 'strong' },
        ],
        rows: [
            { metric: 'Total Orders', current: '6,214', previous: '5,890', change: '+5.5%' },
            { metric: 'Gross Revenue', current: 'Rs. 4.20M', previous: 'Rs. 3.98M', change: '+5.5%' },
            { metric: 'Avg Order Value', current: 'Rs. 676', previous: 'Rs. 675', change: '+0.1%' },
            { metric: 'Fulfillment Rate', current: '85.77%', previous: '84.12%', change: '+1.65pp' },
            { metric: 'Active Customers', current: '3,749', previous: '3,612', change: '+3.8%' },
        ],
    },
    options: { variant: 'report', striped: true },
    reportCodegenDefaultHint:
        'Use DataTableChart with variant report and striped for period-over-period KPI tables. First column = metric name (emphasis primary); numeric columns right-aligned from aggregated query results.',
    code: `// Striped Metrics Table
let table = {
  columns: [{ key: 'metric', label: 'Metric', emphasis: 'primary' }, { key: 'current', label: 'Current', align: 'right' }],
  rows: [{ metric: 'Total Orders', current: '6,214', previous: '5,890', change: '+5.5%' }],
  options: { variant: 'report', striped: true }
};`,
};

const orderHeatmapChart = {
    id: 'heatmap-chart',
    title: 'Order Trend Heatmap',
    type: 'HEATMAP',
    category: 'Special Styles',
    component: HeatmapChart,
    data: {},
    options: {},
    props: {
        rows: heatmapDays,
        cols: heatmapHours,
        values: heatmapValues,
        colorFrom: palette.softBlue,
        colorTo: palette.primary,
        showLegend: true,
    },
    reportCodegenDefaultHint:
        'When query rows expose two categorical dimensions plus one numeric measure (for example hour x weekday), render a HeatmapChart-style grid using patterns from components/charts/HeatmapChart.jsx — values only from result rows.',
    code: `// Heatmap Chart
let heatmap = {
  rows: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  cols: ['00', '01', /* ... */, '23']
};`,
};

const heroCard = {
    id: 'sales-performance',
    title: 'Hero Card',
    type: 'DASHBOARD',
    category: 'Special Styles',
    component: HeroCard,
    data: {},
    options: {},
    // Showcase values sourced here (not from HeroCard's internal defaults).
    props: {
        title: 'Sales Performance',
        badge: 'Report',
        periodPrimary: 'Month-to-Date Analysis · April 2026',
        periodSecondary: 'Period: April 1 - April 28, 2026 · Currency: NPR',
        stats: [
            { label: 'Total Orders', val: '6,214' },
            { label: 'Total Revenue', val: 'Rs. 4.20M' },
            { label: 'Avg Order Value', val: 'Rs. 676' },
            { label: 'Total Units Sold', val: '4,234' },
            { label: 'Active Warehouses', val: '9' },
        ],
        score: 72,
        scoreLabel: 'Health Score',
        ringSegments: [
            { dasharray: '130 263', strokeClass: 'stroke-success' },
            { dasharray: '50 263', dashoffset: '-130', strokeClass: 'stroke-warning' },
            { dasharray: '50 263', dashoffset: '-180', strokeClass: 'stroke-orange-500' },
            { dasharray: '33 263', dashoffset: '-230', strokeClass: 'stroke-danger' },
        ],
        legend: [
            { label: 'Good Performance', val: 5, colorClass: 'bg-success' },
            { label: 'Needs Attention', val: 2, colorClass: 'bg-warning' },
            { label: 'Warning', val: 2, colorClass: 'bg-orange-500' },
            { label: 'Critical', val: 2, colorClass: 'bg-danger' },
        ],
    },
    reportCodegenDefaultHint:
        'Hero must match HeroCard.jsx: full-width gradient banner, left title and meta from timeFrameLabel/dateRange, KPI stats row (default 4-5 headline metrics aggregated from query results), right score ring and legend from computable ratios only — never hardcode library preview numbers.',
    code: `// Sales Performance Dashboard
let report = { score: 72, metrics: { revenue: '4.20M', orders: 6214, units: 4234 } };`,
};

const analyticsOverviewCard = {
    id: 'platform-overview',
    title: 'Analytics Overview',
    type: 'DASHBOARD',
    category: 'Special Styles',
    component: AnalyticsOverviewCard,
    data: {},
    options: {},
    // Showcase values sourced here (not from AnalyticsOverviewCard's internal defaults).
    props: {
        railTitle: 'Platform KPIs',
        railIcon: Zap,
        railSelectorLabel: 'Total Orders',
        railValue: '6,214',
        railUnit: 'orders',
        railSegmentsFilled: 8,
        railAxis: ['0', '5000', '10000'],
        railStatusLabel: 'Order status:',
        railStatusText: 'Healthy',
        summaryTitle: 'Order Flow Summary',
        summaryValue: '85.77%',
        summaryUnit: 'fulfillment rate',
        keyMetricsTitle: 'Key Metrics:',
        keyMetrics: [
            { label: 'Unique Customers', val: '3,749', icon: MousePointer2 },
            { label: 'Delivered Orders', val: '5,330', icon: Activity },
            { label: 'Cancelled Orders', val: '883', icon: Zap },
        ],
        overviewTitle: 'Operations Overview',
        tiles: [
            { label: 'Active Restaurants', val: '248', sub: 'locations', desc: 'Operational across network', icon: Store, badge: 'Live' },
            { label: 'Service Hours', val: '14.5', sub: 'avg hours', desc: 'Daily active operations', icon: Clock, badge: 'Daily' },
            { label: 'Peak Timing', val: '18:00', sub: 'PM', desc: 'Highest order frequency', icon: TrendingUp, badge: 'Peak' },
        ],
        scoreTitle: 'Platform Score',
        gaugeData: {
            labels: ['Fulfilled', 'Remaining'],
            datasets: [{
                data: [85.77, 14.23],
                backgroundColor: [palette.accent, palette.gray],
                borderWidth: 0,
                circumference: 180,
                rotation: -90,
            }],
        },
        gaugeOptions: {
            aspectRatio: 1.8,
            layout: { padding: { bottom: 25 } },
            elements: {
                center: { text: '85.77%', subText: 'Fulfillment Score', fontSize: 22, subFontSize: 8, yOffset: 20 },
            },
        },
        gaugeBadges: [
            { label: 'Delivery Success', val: '85.77%', dotClass: 'bg-primary-600', textClass: 'text-primary-600' },
            { label: 'Customer Activity', val: 'Active', dotClass: 'bg-primary-400', textClass: 'text-primary-400' },
        ],
        insightsTitle: 'Category Insights',
        insights: [
            { label: 'Simplify Navigation', desc: '3,064 categories for 3,823 products.', tag: 'OPTIMIZATION', bgClass: 'bg-warning', textClass: 'text-warning' },
        ],
        showCta: true,
        ctaTitle: 'Set and Achieve Your Growth Goals!',
        ctaSubtitle: 'Your Goal: Reach 6,835.4 Orders',
        ctaButtonLabel: 'Adjust My Goal',
    },
    description: `# Platform Overview\nThis dashboard provides a comprehensive view of the platform's key performance indicators (KPIs).\n\n### Features\n- **Order Tracking**: Monitor real-time order volume.\n- **Fulfillment**: Analyze delivery success rates.\n- **Operational Status**: Check restaurant and warehouse health.`,
    reportCodegenDefaultHint:
        'Include the Platform KPIs rail and Operations Overview block matching AnalyticsOverviewCard.jsx layout and classes; bind every KPI and chart to aggregated query results (topic dashboards: executedQueries; overall: reports). Show N/A or omit a card when a metric cannot be computed — do not copy hardcoded demo values from the reference component.',
    code: `// Platform Overview Dashboard
let dashboard = { kpis: { orders: 6214, health: 'Healthy' }, fulfillment: '85.77%', restaurants: 248, activeScore: 85.77 };`,
};

export const chartsData = [
    analyticsOverviewCard,
    heroCard,
    basicDataTable,
    stripedMetricsTable,
    basicLineChart,
    areaStackedChart,
    gradientLineChart,
    roundedBarChart,
    stackedPerformanceChart,
    marketSharePieChart,
    customDoughnutChart,
    performanceGaugeChart,
    capabilityRadarChart,
    polarAreaChart,
    mixedBarLineChart,
    orderHeatmapChart,
];
